/**
 * Featured Filters
 *
 * Applies SearchSpring filters when clicking featured filter buttons.
 * Toggles filter in URL and dispatches FilterUpdate so results list and active facets stay in sync.
 * Disables buttons for filter values that would return 0 products (from facet counts).
 */

import { ThemeEvents } from '@theme/events';
import { waitForConstructorClient } from '@theme/constructor-utils';
import { scrollIntoView } from '@theme/scrolling';
import { prefersReducedMotion } from '@theme/utilities';

const ACTIVE_CLASS = 'featured-filters--active';
const DISABLED_CLASS = 'featured-filters--disabled';
const FIELD_VALUE_SEPARATOR = '\n';

/**
 * @typedef {{
 *   exact: Map<string, number>,
 *   lower: Map<string, number>,
 *   fields: Set<string>,
 * }} CountIndex
 */

/** @type {CountIndex} */
let facetCountIndex = createEmptyCountIndex();
/** @type {CountIndex} */
let probeCountIndex = createEmptyCountIndex();
/** @type {number} */
let probeGeneration = 0;
/** @type {AbortController | null} */
let activeProbeController = null;

/**
 * @returns {CountIndex}
 */
function createEmptyCountIndex() {
  return {
    exact: new Map(),
    lower: new Map(),
    fields: new Set(),
  };
}

/**
 * @param {Map<string, number>} countMap
 * @returns {CountIndex}
 */
function buildCountIndex(countMap) {
  const index = createEmptyCountIndex();
  for (const [key, count] of countMap) {
    const separatorIndex = key.indexOf(FIELD_VALUE_SEPARATOR);
    if (separatorIndex === -1) continue;
    const field = key.slice(0, separatorIndex);
    const value = key.slice(separatorIndex + FIELD_VALUE_SEPARATOR.length);
    index.fields.add(field);
    index.exact.set(key, count);
    index.lower.set(`${field}${FIELD_VALUE_SEPARATOR}${value.toLowerCase()}`, count);
  }
  return index;
}

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function sanitizeFilterToken(value) {
  return String(value ?? '').replace('tags_', '').trim();
}

/**
 * @param {Map<string, number>} nextProbeCountMap
 */
function setProbeCountMap(nextProbeCountMap) {
  probeCountIndex = buildCountIndex(nextProbeCountMap);
}

/**
 * Build map of "field|value" -> count from facets. Used to disable buttons with 0 results.
 * Keys use \n so we match exactly; Constructor returns field and value.value as-is.
 * @param {Array<{ field: string, values: Array<{ value: string, count?: number }> }>} facets
 */
function updateFacetCountMap(facets) {
  const nextFacetCountMap = new Map();
  if (!Array.isArray(facets)) {
    facetCountIndex = createEmptyCountIndex();
    return;
  }
  facets.forEach((facet) => {
    const field = facet.field;
    (facet.values || []).forEach((v) => {
      const key = `${field}${FIELD_VALUE_SEPARATOR}${v.value}`;
      nextFacetCountMap.set(key, typeof v.count === 'number' ? v.count : 0);
    });
  });
  facetCountIndex = buildCountIndex(nextFacetCountMap);
}

/**
 * Build filters payload from URL filter.* params.
 * @returns {Record<string, string[]>}
 */
function getFiltersFromUrl() {
  /** @type {Record<string, string[]>} */
  const filters = {};
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (!key.startsWith('filter.')) return;
    const field = key.slice('filter.'.length);
    const existing = filters[field] || [];
    filters[field] = [...existing, value];
  });
  return filters;
}

/**
 * @param {Record<string, string[]>} baseFilters
 * @param {string} field
 * @param {string} value
 * @returns {Record<string, string[]>}
 */
function getFiltersWithCandidate(baseFilters, field, value) {
  const existing = baseFilters[field] || [];
  if (existing.includes(value)) return baseFilters;
  return {
    ...baseFilters,
    [field]: [...existing, value],
  };
}

/**
 * @returns {{ isBrowseMode: boolean, browseGroupId: string, query: string } | null}
 */
function getConstructorContext() {
  const resultsList = document.querySelector('constructor-results-list');
  if (!(resultsList instanceof HTMLElement)) return null;

  const isBrowseMode =
    resultsList.hasAttribute('data-cnstrc-browse') || resultsList.dataset.context === 'collection';
  const browseGroupId =
    (resultsList.dataset.collectionHandle || resultsList.dataset.cnstrcFilterValue || '').trim() ||
    (() => {
      const parts = window.location.pathname.split('/').filter(Boolean);
      const collectionIndex = parts.indexOf('collections');
      if (collectionIndex === -1) return '';
      const handle = parts[collectionIndex + 1] || '';
      return decodeURIComponent(handle);
    })();
  const query =
    new URLSearchParams(window.location.search).get('q') || resultsList.dataset.searchQuery || '';

  if (isBrowseMode && !browseGroupId) return null;
  if (!isBrowseMode && !query) return null;
  return { isBrowseMode, browseGroupId, query };
}

/**
 * Probe total counts for featured text filters when API facets omit those fields.
 * @param {string[]} missingFields
 */
async function probeMissingFeaturedTextFieldCounts(missingFields) {
  const generation = ++probeGeneration;
  activeProbeController?.abort();
  const controller = new AbortController();
  activeProbeController = controller;

  try {
    const missingFieldSet = new Set(missingFields.map((field) => sanitizeFilterToken(field)));
    /** @type {Array<{ field: string, value: string }>} */
    const candidates = [];
    const seen = new Set();

    document
      .querySelectorAll('button.featured-filters-text-filter[data-cnstrc-featured-filter]')
      .forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        const field = sanitizeFilterToken(button.getAttribute('data-filter-field'));
        const value = sanitizeFilterToken(button.getAttribute('data-filter-value'));
        if (!field || !value || !missingFieldSet.has(field)) return;
        const key = `${field}${FIELD_VALUE_SEPARATOR}${value}`;
        if (seen.has(key)) return;
        seen.add(key);
        candidates.push({ field, value });
      });

    if (!candidates.length) {
      setProbeCountMap(new Map());
      syncActiveState();
      return;
    }

    const context = getConstructorContext();
    if (!context) return;

    const constructorioClient = await waitForConstructorClient({
      signal: controller.signal,
      predicate: (client) =>
        context.isBrowseMode
          ? Boolean(client?.browse?.getBrowseResults)
          : Boolean(client?.search?.getSearchResults),
    });

    if (!constructorioClient || generation !== probeGeneration || controller.signal.aborted) return;
    const baseFilters = getFiltersFromUrl();

    const countEntries = await Promise.allSettled(
      candidates.map(async ({ field, value }) => {
        const filters = getFiltersWithCandidate(baseFilters, field, value);
        const requestOptions = {
          page: 1,
          resultsPerPage: 1,
          ...(Object.keys(filters).length > 0 ? { filters } : {}),
        };
        const response = context.isBrowseMode
          ? await constructorioClient.browse.getBrowseResults(
              'group_id',
              context.browseGroupId,
              requestOptions,
            )
          : await constructorioClient.search.getSearchResults(context.query, requestOptions);
        const totalNumResults = Number(response?.response?.total_num_results ?? 0);
        return {
          key: `${field}${FIELD_VALUE_SEPARATOR}${value}`,
          count: Number.isFinite(totalNumResults) ? totalNumResults : 0,
        };
      }),
    );

    if (generation !== probeGeneration || controller.signal.aborted) return;

    /** @type {Array<{ key: string, count: number }>} */
    const fulfilledEntries = [];
    countEntries.forEach((entry) => {
      if (entry.status !== 'fulfilled') return;
      fulfilledEntries.push(entry.value);
    });

    // Keep last good probe map when every request in this run fails.
    if (!fulfilledEntries.length) return;

    setProbeCountMap(new Map(fulfilledEntries.map((entry) => [entry.key, entry.count])));
    syncActiveState();
  } catch (error) {
    if (/** @type {any} */ (error)?.name === 'AbortError') return;
    // Fail soft: preserve the last successful probe map.
  } finally {
    if (activeProbeController === controller) {
      activeProbeController = null;
    }
  }
}

// Register immediately so we never miss the event (it may fire before DOMContentLoaded).
document.addEventListener('cnstrc:results-loaded', (event) => {
  const customEvent = /** @type {CustomEvent<{ facets?: Array<{ field: string, values: Array<{ value: string, count?: number }> }> }>} */ (event);
  const normalizedFacets = Array.isArray(customEvent.detail?.facets) ? customEvent.detail.facets : [];
  updateFacetCountMap(normalizedFacets);

  const facetFieldSet = new Set(normalizedFacets.map((facet) => String(facet?.field || '')));
  const featuredTextFieldSet = new Set();
  document
    .querySelectorAll('button.featured-filters-text-filter[data-cnstrc-featured-filter]')
    .forEach((button) => {
      const field = sanitizeFilterToken(button.getAttribute('data-filter-field'));
      if (!field) return;
      featuredTextFieldSet.add(field);
    });
  const missingFeaturedTextFields = [...featuredTextFieldSet].filter(
    (field) => !facetFieldSet.has(field),
  );

  syncActiveState();
  void probeMissingFeaturedTextFieldCounts(missingFeaturedTextFields);
});

/**
 * Get count for a field+value from an index (exact key, or case-insensitive value match).
 * @param {CountIndex} index
 * @param {string} field
 * @param {string} value
 * @returns {number | undefined} count if found
 */
function getIndexedCountForFieldValue(index, field, value) {
  const sanitizedField = sanitizeFilterToken(field);
  const sanitizedValue = sanitizeFilterToken(value);
  if (!sanitizedField || !sanitizedValue) return undefined;

  const exactKey = `${sanitizedField}${FIELD_VALUE_SEPARATOR}${sanitizedValue}`;
  if (index.exact.has(exactKey)) return index.exact.get(exactKey);
  return index.lower.get(`${sanitizedField}${FIELD_VALUE_SEPARATOR}${sanitizedValue.toLowerCase()}`);
}

/**
 * Whether index data exists for this field.
 * @param {CountIndex} index
 * @param {string} field
 * @returns {boolean}
 */
function hasIndexedDataForField(index, field) {
  const sanitizedField = sanitizeFilterToken(field);
  if (!sanitizedField) return false;
  return index.fields.has(sanitizedField);
}

/**
 * Sync active state and disabled state of featured filter buttons from current URL and facet counts.
 * Disable when: we have facet data for this field and (count is 0 or value is missing from response).
 * Constructor often returns only facet values with count > 0, so "missing" means 0 results.
 */
function syncActiveState() {
  const urlParams = new URLSearchParams(window.location.search);
  document.querySelectorAll('button[data-cnstrc-featured-filter]').forEach((btn) => {
    if (!(btn instanceof HTMLButtonElement)) return;
    const field = sanitizeFilterToken(btn.getAttribute('data-filter-field'));
    const value = sanitizeFilterToken(btn.getAttribute('data-filter-value'));
    if (!field || !value) return;
    const paramName = `filter.${field}`;
    const currentValues = urlParams.getAll(paramName);
    const isActive = currentValues.includes(value);
    btn.classList.toggle(ACTIVE_CLASS, isActive);
    btn.setAttribute('aria-pressed', String(isActive));

    const facetCount = getIndexedCountForFieldValue(facetCountIndex, field, value);
    const probeCount = getIndexedCountForFieldValue(probeCountIndex, field, value);
    const count = facetCount ?? probeCount;
    const hasDataForField =
      hasIndexedDataForField(facetCountIndex, field) || hasIndexedDataForField(probeCountIndex, field);
    const hasZeroResults =
      (hasDataForField && count === undefined) || (count !== undefined && count === 0);
    const shouldDisable = hasZeroResults && !isActive;
    btn.classList.toggle(DISABLED_CLASS, shouldDisable);
    btn.disabled = shouldDisable;
  });
}

/**
 * Toggle a single filter in the URL: add if not present, remove if present.
 * @param {string} field - Constructor facet field name
 * @param {string} value - Filter value
 */
function toggleFilterInUrl(field, value) {
  const sanitizedField = sanitizeFilterToken(field);
  const url = new URL(window.location.href);
  const paramName = `filter.${sanitizedField}`;
  const values = url.searchParams.getAll(paramName);

  url.searchParams.delete(paramName);
  if (values.includes(value)) {
    values.filter((v) => v !== value).forEach((v) => url.searchParams.append(paramName, v));
  } else {
    values.forEach((v) => url.searchParams.append(paramName, v));
    url.searchParams.append(paramName, value);
  }
  url.searchParams.delete('page');

  history.pushState({}, '', url.toString());
  performance.mark('facet-filter-dispatched');
  document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
}

/**
 * Open the matching drawer facet accordion for a featured filter that opens the
 * filters drawer (e.g. "Color", "Size") and anchor the drawer scroll position to
 * it. Facets render asynchronously, so retry over a few animation frames until the
 * facet exists, then expand it (mobile accordion) and scroll it into view.
 * @param {string | null | undefined} field - Constructor facet field (e.g. "color_group", "size").
 */
function openDrawerFacetAccordion(field) {
  const sanitizedField = sanitizeFilterToken(field);
  if (!sanitizedField) return;

  // Match both the mobile accordion and the desktop (non-accordion) facet variant.
  const selector = `.cnstrc-facets--drawer [data-facet-field="${CSS.escape(sanitizedField)}"]`;
  let attempts = 0;
  const maxAttempts = 10;

  const tryOpen = () => {
    const facet = document.querySelector(selector);
    if (facet instanceof HTMLElement) {
      const details = facet.querySelector('details');
      if (details instanceof HTMLDetailsElement) {
        details.open = true;
      }
      anchorDrawerToFacet(facet);
      return;
    }
    if (++attempts < maxAttempts) {
      requestAnimationFrame(tryOpen);
    }
  };

  tryOpen();
}

/**
 * Scroll the drawer's scrollable filters container so the given facet is anchored
 * to the top. Runs after a frame so any accordion expansion is reflected in layout.
 * @param {HTMLElement} facet - The facet element to anchor to.
 */
function anchorDrawerToFacet(facet) {
  const scrollContainer = facet.closest('.facets-drawer__filters');
  if (!(scrollContainer instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    scrollIntoView(facet, {
      ancestor: scrollContainer,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'start',
    });
  });
}

function init() {
  document.addEventListener('click', (e) => {
    const clickTarget = e.target;
    if (!(clickTarget instanceof Element)) return;

    const drawerFacetBtn = clickTarget.closest('[data-open-facet-field]');
    if (drawerFacetBtn instanceof HTMLButtonElement) {
      openDrawerFacetAccordion(drawerFacetBtn.getAttribute('data-open-facet-field'));
    }

    const btn = clickTarget.closest('[data-cnstrc-featured-filter]');
    if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
    const field = sanitizeFilterToken(btn.getAttribute('data-filter-field'));
    const value = sanitizeFilterToken(btn.getAttribute('data-filter-value'));
    if (!field || !value) return;

    toggleFilterInUrl(field, value);
    syncActiveState();
  });

  document.addEventListener(ThemeEvents.FilterUpdate, syncActiveState);
  syncActiveState();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
